// SquareCardForm — wraps Square Web Payments SDK to tokenize a real card and return
// a source token (similar shape to Stripe's pm_xxx). The token can then be sent to the
// backend (Square Payments API) to actually charge the card. PCI scope stays in Square's
// hosted iframe — we never see the raw PAN.
//
// Usage:
//   <SquareCardForm onToken={(token, details) => save(token, details.last4, details.brand)} />

import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Lock } from "lucide-react";

const SDK_SRC = {
  production: "https://web.squarecdn.com/v1/square.js",
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
};

let sdkPromise = null;
function loadSquareSdk(env) {
  if (window.Square) return Promise.resolve(window.Square);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SDK_SRC[env] || SDK_SRC.production;
    s.async = true;
    s.onload = () => resolve(window.Square);
    s.onerror = () => { sdkPromise = null; reject(new Error("Failed to load Square SDK")); };
    document.head.appendChild(s);
  });
  return sdkPromise;
}

export default function SquareCardForm({ onToken, busy: parentBusy = false, label = "Tokenize Card" }) {
  const cardContainerRef = useRef(null);
  const cardInstanceRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [loadingSdk, setLoadingSdk] = useState(true);
  const [tokenizing, setTokenizing] = useState(false);
  const [error, setError] = useState(null);

  // 1) Get the public Square config (app_id + location_id + env) from backend
  useEffect(() => {
    let cancelled = false;
    api.get("/payments/square/public-config")
      .then(r => { if (!cancelled) setConfig(r.data); })
      .catch(() => { if (!cancelled) setConfig({ enabled: false }); });
    return () => { cancelled = true; };
  }, []);

  // 2) Load Square SDK + attach the card form
  useEffect(() => {
    if (!config || !config.enabled) return;
    let cancelled = false;
    let attached = null;
    (async () => {
      try {
        setLoadingSdk(true);
        const Square = await loadSquareSdk(config.environment || "production");
        if (cancelled) return;
        const payments = Square.payments(config.application_id, config.location_id);
        const card = await payments.card({
          // v2.7.0 batch DE · Square Web SDK rejects `backgroundColor` on the
          // .input-container selector — only `borderColor` / `borderRadius`
          // are allowed there. `backgroundColor` stays valid on `input`
          // itself. Slimmed style spec eliminates the runtime SDK warning
          // and keeps the high-contrast dark-on-white card text.
          style: {
            ".input-container": {
              borderColor: "#3f3f46",
              borderRadius: "6px",
            },
            ".input-container.is-focus": { borderColor: "#fbbf24" },
            ".input-container.is-error": { borderColor: "#f87171" },
            ".message-text": { color: "#52525b" },
            ".message-icon": { color: "#52525b" },
            ".message-text.is-error": { color: "#dc2626" },
            input: {
              color: "#0f172a",
              backgroundColor: "#ffffff",
              fontSize: "16px",
              fontWeight: "500",
            },
            "input.is-error": { color: "#0f172a" },
            "input::placeholder": { color: "#94a3b8" },
          },
        });
        if (cancelled) return;
        await card.attach(cardContainerRef.current);
        attached = card;
        cardInstanceRef.current = card;
        setLoadingSdk(false);
      } catch (e) {
        if (!cancelled) { setError(e.message || "Failed to load Square card form"); setLoadingSdk(false); }
      }
    })();
    return () => {
      cancelled = true;
      if (attached) { try { attached.destroy?.(); } catch {} cardInstanceRef.current = null; }
    };
  }, [config?.application_id, config?.location_id, config?.environment, config?.enabled]);

  const tokenize = async () => {
    if (!cardInstanceRef.current || tokenizing) return;
    setError(null); setTokenizing(true);
    try {
      const result = await cardInstanceRef.current.tokenize();
      if (result.status === "OK") {
        // result.token is the source_id we send to Square /v2/payments
        onToken?.(result.token, result.details || {});
      } else {
        const msg = (result.errors || []).map(e => e.message || e.detail).join("; ") || "Tokenization failed";
        setError(msg);
      }
    } catch (e) {
      setError(e.message || "Tokenization failed");
    } finally { setTokenizing(false); }
  };

  if (config && !config.enabled) {
    return (
      <div className="p-3 rounded bg-zinc-900 border border-amber-400/40 text-xs text-amber-300" data-testid="square-not-configured">
        Square is not enabled by the dispatcher yet — your card will be saved with metadata only and charged in person at pickup.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="square-card-form">
      {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/40 rounded p-2" data-testid="square-card-error">{error}</div>}
      <div className="p-3 rounded bg-zinc-950 border border-zinc-700">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
          <Lock className="h-3 w-3 text-green-400"/> Powered by Square · PCI-secure (your card data never touches our servers)
        </div>
        <div ref={cardContainerRef} data-testid="square-card-container" style={{ minHeight: 90 }}/>
        {loadingSdk && <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/>Loading Square…</div>}
      </div>
      <Button
        data-testid="square-tokenize-btn"
        onClick={tokenize}
        disabled={loadingSdk || tokenizing || parentBusy}
        className="w-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
      >
        {tokenizing ? <Loader2 className="h-4 w-4 animate-spin"/> : label}
      </Button>
    </div>
  );
}
